"use client";

import { useCallback, useEffect, useMemo, useState } from "react"

import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { ChevronDownIcon } from "@/tiptap/icons/chevron-down-icon"
import { FONT_SIZE_PRESETS, normalizeFontSize } from "@/tiptap/extensions/font-size-extension"
import { getActiveMarkAttrs } from "@/tiptap/lib/tiptap-advanced-utils"
import { isMarkInSchema, isNodeTypeSelected } from "@/tiptap/lib/tiptap-utils"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"
import { Input } from "@/tiptap/ui-primitive/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"

const NON_TEXT_STYLEABLE_NODE_TYPES = ["image", "youtube", "iframe", "audio", "video"]

export function canSetFontSize(editor) {
  if (!editor || !editor.isEditable) return false
  if (
    !isMarkInSchema("textStyle", editor) ||
    isNodeTypeSelected(editor, NON_TEXT_STYLEABLE_NODE_TYPES)
  )
    return false

  try {
    return editor.can().setFontSize("16px")
  } catch {
    return false
  }
}

export function shouldShowFontSizePopover(params) {
  const { editor, hideWhenUnavailable } = params

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isMarkInSchema("textStyle", editor)) return false

  if (!editor.isActive("code")) {
    return canSetFontSize(editor)
  }

  return true
}

export function useFontSizePopover(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onFontSizeChanged,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)

  const activeTextStyle = getActiveMarkAttrs(editor, "textStyle") || {}
  const activeFontSize = normalizeFontSize(activeTextStyle.fontSize) || null
  const canToggle = canSetFontSize(editor)

  useEffect(() => {
    if (!editor) return

    const updateVisibility = () => {
      setIsVisible(shouldShowFontSizePopover({
        editor,
        hideWhenUnavailable,
      }))
    }

    updateVisibility()

    editor.on("selectionUpdate", updateVisibility)

    return () => {
      editor.off("selectionUpdate", updateVisibility)
    }
  }, [editor, hideWhenUnavailable])

  const handleFontSizeChanged = useCallback((value) => {
    if (!editor) {
      return false
    }

    const normalizedFontSize = normalizeFontSize(value)

    if (!normalizedFontSize) {
      return false
    }

    const didApply = editor.chain().focus().setFontSize(normalizedFontSize).run()

    if (didApply) {
      onFontSizeChanged?.({ value: normalizedFontSize })
    }

    return didApply
  }, [editor, onFontSizeChanged])

  const handleFontSizeUnset = useCallback(() => {
    if (!editor) {
      return false
    }

    const didUnset = editor.chain().focus().unsetFontSize().run()

    if (didUnset) {
      onFontSizeChanged?.({ value: null })
    }

    return didUnset
  }, [editor, onFontSizeChanged])

  return {
    isVisible,
    canToggle,
    activeFontSize,
    handleFontSizeChanged,
    handleFontSizeUnset,
    label: "Font size",
  }
}

function FontSizePanel({
  activeFontSize,
  onApply,
  onReset,
}) {
  const [customValue, setCustomValue] = useState(activeFontSize || "")
  const normalizedCustomValue = normalizeFontSize(customValue)

  useEffect(() => {
    setCustomValue(activeFontSize || "")
  }, [activeFontSize])

  const presetButtons = useMemo(() => FONT_SIZE_PRESETS, [])

  const stopEditorHotkeys = useCallback((event) => {
    event.stopPropagation()
  }, [])

  return (
    <Card
      role="menu"
      style={{ minWidth: "18rem" }}
      onKeyDownCapture={stopEditorHotkeys}>
      <CardBody>
        <CardItemGroup>
          <CardGroupLabel>Font size</CardGroupLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
            {presetButtons.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant="ghost"
                data-size="small"
                data-active-state={activeFontSize === preset ? "on" : "off"}
                aria-label={`Set font size to ${preset}`}
                onClick={() => onApply(preset)}>
                <span className="tiptap-button-text">{preset}</span>
              </Button>
            ))}
          </div>
        </CardItemGroup>

        <CardItemGroup>
          <CardGroupLabel>Custom</CardGroupLabel>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Input
              value={customValue}
              onChange={(event) => setCustomValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && normalizedCustomValue) {
                  event.preventDefault()
                  onApply(normalizedCustomValue)
                }
              }}
              placeholder="16px"
              aria-label="Custom font size"
              style={{ flex: 1 }} />
            <Button
              type="button"
              variant="ghost"
              disabled={!normalizedCustomValue}
              data-disabled={!normalizedCustomValue}
              onClick={() => {
                if (normalizedCustomValue) {
                  onApply(normalizedCustomValue)
                }
              }}>
              <span className="tiptap-button-text">Apply</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={!activeFontSize}
              data-disabled={!activeFontSize}
              onClick={onReset}>
              <span className="tiptap-button-text">Reset</span>
            </Button>
          </div>
        </CardItemGroup>
      </CardBody>
    </Card>
  )
}

export function FontSizePopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onFontSizeChanged,
  children,
  ref,
  ...buttonProps
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = useState(false)
  const {
    isVisible,
    canToggle,
    activeFontSize,
    handleFontSizeChanged,
    handleFontSizeUnset,
    label,
  } = useFontSizePopover({
    editor,
    hideWhenUnavailable,
    onFontSizeChanged,
  })

  if (!isVisible) return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-appearance="default"
          role="button"
          aria-label={label}
          tooltip={label}
          disabled={!canToggle}
          data-disabled={!canToggle}
          ref={ref}
          {...buttonProps}>
          {children ?? (
            <>
              <span
                className="tiptap-button-text"
                style={{ minWidth: "2.5rem", flexGrow: 0 }}>
                {activeFontSize || "16px"}
              </span>
              <ChevronDownIcon className="tiptap-button-dropdown-small" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Font size options" side="bottom" align="start">
        <FontSizePanel
          activeFontSize={activeFontSize}
          onApply={(value) => {
            if (handleFontSizeChanged(value)) {
              setIsOpen(false)
            }
          }}
          onReset={() => {
            if (handleFontSizeUnset()) {
              setIsOpen(false)
            }
          }} />
      </PopoverContent>
    </Popover>
  )
}

export default FontSizePopover
